/**
 * Origin editor for a question (admin) — see docs/FeatureDesigns/question-pools-and-entitlement/question-source-type-target.md.
 * Renders the sourceType select and, per origin, its structured detail:
 *   textbook → bookSources[] (picked from the book registry) + location
 *   online   → sourceUrl
 * previous-year-paper / ai-generated keep their existing detail elsewhere
 * (appearedInExams / sourcePdfBlobPath), so nothing extra here.
 */
import { useState } from 'react';
import { QUESTION_SOURCE_TYPES, type BookOption, type BookRef, type QuestionFormData, type QuestionSourceType, type UpdateField } from '../formTypes';

const LABELS: Record<QuestionSourceType, string> = {
    'previous-year-paper': 'Previous-year paper',
    'textbook': 'Textbook',
    'ai-generated': 'AI-generated',
    'online': 'Online',
    'manual': 'Manual (hand-authored)',
    'unknown': 'Unknown',
};

export function QuestionOriginFields({
    form,
    updateField,
    bookOptions,
}: {
    form: QuestionFormData;
    updateField: UpdateField;
    bookOptions?: BookOption[];
}) {
    const [pickBookId, setPickBookId] = useState('');
    const [location, setLocation] = useState('');

    const sourceType = form.sourceType;
    const bookSources = form.bookSources ?? [];

    const addBook = () => {
        const book = bookOptions?.find(b => b.id === pickBookId);
        if (!book) return;
        if (bookSources.some(b => b.bookId === book.id && (b.location ?? '') === location.trim())) return;
        const next: BookRef[] = [...bookSources, { bookId: book.id, bookName: book.title, location: location.trim() || undefined }];
        updateField('bookSources', next);
        setPickBookId('');
        setLocation('');
    };

    const removeBook = (idx: number) => {
        const next = bookSources.filter((_, i) => i !== idx);
        updateField('bookSources', next.length ? next : undefined);
    };

    return (
        <div className="form-section">
            <label>Source / origin</label>
            <select
                className="form-input"
                value={sourceType ?? ''}
                onChange={e => updateField('sourceType', (e.target.value || undefined) as QuestionSourceType | undefined)}
            >
                <option value="">— not set —</option>
                {QUESTION_SOURCE_TYPES.map(t => <option key={t} value={t}>{LABELS[t]}</option>)}
            </select>

            {/* Legacy free-text provenance, shown for context when present. */}
            {form.questionSource && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    Legacy source: {form.questionSource}
                </div>
            )}

            {/* textbook → book references */}
            {sourceType === 'textbook' && (
                <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                    {bookSources.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {bookSources.map((b, i) => (
                                <span key={`${b.bookId}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'var(--bg-tertiary, #2a2a35)' }}>
                                    {b.bookName}{b.location ? ` · ${b.location}` : ''}
                                    <button type="button" title="Remove" onClick={() => removeBook(i)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                                </span>
                            ))}
                        </div>
                    )}
                    {bookOptions
                        ? (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <select className="form-input" style={{ flex: '1 1 200px' }} value={pickBookId} onChange={e => setPickBookId(e.target.value)}>
                                    <option value="">— pick a book —</option>
                                    {bookOptions.map(b => (
                                        <option key={b.id} value={b.id}>{b.title}{b.author ? ` — ${b.author}` : ''}</option>
                                    ))}
                                </select>
                                <input className="form-input" style={{ flex: '1 1 160px' }} placeholder="Location (e.g. Ch 4 · Ex 1.2 · Q15)" value={location} onChange={e => setLocation(e.target.value)} />
                                <button type="button" className="btn-secondary" disabled={!pickBookId} onClick={addBook}>Add</button>
                            </div>
                        )
                        : <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Book registry unavailable — manage books under Content › Books.</div>}
                </div>
            )}

            {/* online → source URL */}
            {sourceType === 'online' && (
                <div style={{ marginTop: 10 }}>
                    <input
                        className="form-input"
                        type="url"
                        placeholder="https://…"
                        value={form.sourceUrl ?? ''}
                        onChange={e => updateField('sourceUrl', e.target.value || undefined)}
                    />
                </div>
            )}
        </div>
    );
}
